import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocale } from '../../contexts/LocaleContext';
import useHardwareFirstMembershipTerms from '../../hooks/useHardwareFirstMembershipTerms';
import { formatHardwarePrice, lowestMonthlyPrice } from '../../lib/hardwareFirst';
import * as types from '../../types';
import styles from './HardwareFirstDisclosure.module.scss';

/** the first-term/renewal line per renewal length; a term the copy does not know is not listed */
const TERM_LINE_KEYS: Record<number, string> = {
  6: 'hardware_first_term_6m',
  12: 'hardware_first_term_1y',
  24: 'hardware_first_term_2y',
};

interface HardwareFirstDisclosureProps {
  discount?: types.HardwareDiscountPreview;
  /** the promotion code the quoted first-tracker price depends on, named under the price */
  promoCode?: string;
  /** omit the required-plan sentence when the surrounding ui already quotes the monthly price */
  hidePlanLine?: boolean;
}

/**
 * Hardware-first PDP block: server-priced hardware with strikethrough, the required-plan line, and
 * an expandable first-term/renewal price disclosure. Placeholder fidelity — the E6 design pass
 * swaps copy and layout; the i18n keys and structure are the contract.
 */
export default function HardwareFirstDisclosure({ discount, promoCode, hidePlanLine }: HardwareFirstDisclosureProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const terms = useHardwareFirstMembershipTerms();
  const lowestMonthly = lowestMonthlyPrice(terms);
  const [expanded, setExpanded] = useState(false);

  return (
    <div className={styles.container}>
      {discount && (
        <div className={styles.priceRow}>
          {discount.listPriceInCents > discount.discountedPriceInCents && (
            <span className={styles.listPrice}>{formatHardwarePrice(discount.listPriceInCents, locale)}</span>
          )}
          <span className={styles.discountedPrice}>{formatHardwarePrice(discount.discountedPriceInCents, locale)}</span>
          {discount.promoDiscountInCents > 0 && promoCode && (
            <span className={styles.withCode}>{t('hardware_first_pdp_with_code', { code: promoCode })}</span>
          )}
        </div>
      )}
      <div className={styles.planLine}>
        {!hidePlanLine && (
          <>
            {lowestMonthly
              ? t('hardware_first_plan_required_line', { monthly: lowestMonthly })
              : t('hardware_first_membership_after_activation')}{' '}
          </>
        )}
        <button className={styles.learnMore} onClick={() => setExpanded(!expanded)} type="button">
          {t(hidePlanLine ? 'hardware_first_learn_more_about_membership' : 'learn_more')}
        </button>
      </div>
      {expanded && (
        <ul className={styles.termList}>
          <li>{t('hardware_first_membership_after_activation')}</li>
          {terms.map((term) =>
            TERM_LINE_KEYS[term.renewalMonths] ? (
              <li key={term.renewalMonths}>
                {t(TERM_LINE_KEYS[term.renewalMonths], { intro: term.intro, renewal: term.renewal })}
              </li>
            ) : null,
          )}
          <li>{t('hardware_first_membership_not_required_for_swap')}</li>
        </ul>
      )}
    </div>
  );
}
